import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, DollarSign, ChevronRight, Loader2, 
  AlertTriangle, BarChart3, Building2, Percent
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import '@/styles/cards/mydesk-cards.css';
import '@/styles/kmd-sheet.css';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Series } from '@/components/dashboard/seriesContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProfitByProjectWidget({ darkMode = false, compact = false, limit = 5 }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    fetchProfitData();
  }, []);
  
  const fetchProfitData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/financials/profit-by-project`);
      if (res.data.success) {
        setData(res.data);
      }
    } catch (err) {
      console.error('Error fetching profit data:', err);
      setError('Failed to load profit data');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0';
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000000) {
      return `${amount < 0 ? '-' : ''}$${(absAmount / 1000000).toFixed(1)}M`;
    }
    if (absAmount >= 1000) {
      return `${amount < 0 ? '-' : ''}$${(absAmount / 1000).toFixed(1)}K`;
    }
    return `${amount < 0 ? '-' : ''}$${absAmount.toFixed(0)}`;
  };
  
  const formatFullCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // DESIGN PASS 24 — loading and error are STATES OF THE CARD, not different
  // cards. They kept the title block and series marker off the desk and made
  // this the one card that could measure as missing both.
  if (loading) {
    return (
      <div className="kmd-sheet" data-testid="profit-by-project-card">
        <div className="kmd-sheet__block">
          <span className="kmd-sheet__series"><Series id="profit-by-project" /></span>
          <span className="kmd-sheet__name">Profit by Project</span>
          <span className="kmd-sheet__meta" data-testid="profit-loading">Loading</span>
        </div>
        <div className="kmd-sheet__body">
          <div className="kmc-empty">
            <Loader2 className="w-5 h-5 animate-spin mx-auto kmc-mark" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="kmd-sheet" data-testid="profit-by-project-card">
        <div className="kmd-sheet__block">
          <span className="kmd-sheet__series"><Series id="profit-by-project" /></span>
          <span className="kmd-sheet__name">Profit by Project</span>
          <span className="kmd-sheet__meta" data-testid="profit-error">No data</span>
        </div>
        <div className="kmd-sheet__body">
          <p className="kmd-row__who" data-testid="profit-error-detail">
            {error || 'No project has financial data yet.'}
          </p>
        </div>
      </div>
    );
  }
  
  const { summary, projects } = data;
  const topProjects = projects.slice(0, limit);
  
  // Determine overall health
  const overallHealth = summary.overall_margin_percent >= 20 ? 'excellent' : 
                        summary.overall_margin_percent >= 10 ? 'good' : 
                        summary.overall_margin_percent >= 0 ? 'caution' : 'critical';
  
  const healthColors = {
    excellent: { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' },
    good: { bg: 'bg-blue-100', text: 'text-blue-700', bar: 'bg-blue-500' },
    caution: { bg: 'bg-amber-100', text: 'text-amber-700', bar: 'bg-amber-500' },
    critical: { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' }
  };
  
  if (compact) {
    // DESIGN PASS 16 — C.5 on My Desk: the sheet pattern. The whole sheet is
    // the target, as it was, so no control is added or lost.
    return (
      <div
        className="kmd-sheet kmd-sheet--tap"
        onClick={() => navigate('/financials')}
        data-testid="profit-by-project-card"
      >
        <div className="kmd-sheet__block">
          <span className="kmd-sheet__series"><Series id="profit-by-project" /></span>
          <span className="kmd-sheet__name">Profit by Project</span>
          <span className="kmd-sheet__meta" data-testid="profit-margin">
            {summary.overall_margin_percent}% Margin
          </span>
        </div>
        <div className="kmd-sheet__body kmd-sheet__body--list">
          <div className="kmd-summary kmd-summary--2">
            <div data-testid="profit-revenue">
              <span className="kmd-figure kmd-figure--sm">{formatCurrency(summary.total_revenue)}</span>
              <span className="kmd-figure__label">Revenue</span>
            </div>
            <div data-testid="profit-total">
              <span className={`kmd-figure kmd-figure--sm${summary.total_profit < 0 ? ' kmd-figure--late' : ''}`}>
                {formatCurrency(summary.total_profit)}
              </span>
              <span className="kmd-figure__label">Profit</span>
            </div>
          </div>

          <div className="kmd-meter" aria-hidden="true">
            <div
              className="kmd-meter__fill"
              data-tone={summary.overall_margin_percent < 0 ? 'late' : summary.overall_margin_percent < 10 ? 'wait' : undefined}
              style={{ width: `${Math.min(100, Math.max(0, summary.overall_margin_percent))}%` }}
            />
          </div>

          <p className="kmd-group kmd-tick">
            {summary.profitable_projects} profitable · {summary.unprofitable_projects} negative
          </p>

          {topProjects.length === 0 ? (
            <p className="kmd-row__who" data-testid="profit-empty">No projects with financial data yet.</p>
          ) : topProjects.map((project) => (
            <div className="kmd-row" key={project.project_id} data-testid={`profit-row-${project.project_id}`}>
              <span className="min-w-0">
                <span className="kmd-name">{project.project_name}</span>
                <span className="kmd-row__who">{project.client_name || 'No client'}</span>
              </span>
              <span>
                <span className="kmd-row__amount" data-tone={project.profit < 0 ? 'late' : undefined}>
                  {formatCurrency(project.profit)}
                </span>
                <span className="kmd-tick">{project.margin_percent}%</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Full widget view
  return (
    <Card className={`${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-base flex items-center gap-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
            <BarChart3 className="w-5 h-5 text-[#70170F]" />
            Profit by Project
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs"
            onClick={() => navigate('/financials')}
          >
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'} border ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
            <span className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>Revenue</span>
            <p className={`text-lg font-bold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
              {formatCurrency(summary.total_revenue)}
            </p>
          </div>
          
          <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'} border ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
            <span className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>Costs</span>
            <p className={`text-lg font-bold text-red-600`}>
              {formatCurrency(summary.total_costs)}
            </p>
          </div>
          
          <div className={`p-3 rounded-lg ${healthColors[overallHealth].bg} border`}>
            <span className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>Profit</span>
            <p className={`text-lg font-bold ${healthColors[overallHealth].text}`}>
              {formatCurrency(summary.total_profit)}
            </p>
            <p className={`text-xs ${healthColors[overallHealth].text}`}>
              {summary.overall_margin_percent}% margin
            </p>
          </div>
        </div>
        
        {/* Project List */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={`text-xs font-medium ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>
              Top Projects by Profit
            </span>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-green-600">{summary.profitable_projects} profitable</span>
              <span className="text-gray-400">|</span>
              <span className="text-red-600">{summary.unprofitable_projects} negative</span>
            </div>
          </div>
          
          {topProjects.length === 0 ? (
            <div className="text-center py-4 text-sm text-gray-500">
              No projects with financial data yet
            </div>
          ) : (
            <div className="space-y-2">
              {topProjects.map((project) => {
                const isProfitable = project.profit >= 0;
                const marginColor = project.margin_percent >= 20 ? 'text-green-600' : 
                                   project.margin_percent >= 10 ? 'text-blue-600' : 
                                   project.margin_percent >= 0 ? 'text-amber-600' : 'text-red-600';
                
                return (
                  <TooltipProvider key={project.project_id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className={`p-2 rounded-lg border cursor-pointer hover:shadow-sm transition-all ${
                            darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] hover:border-[#3A3A3A]' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => navigate(`/projects/${project.project_id}/hub`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className={`w-6 h-6 rounded flex items-center justify-center ${
                                isProfitable ? 'bg-green-100' : 'bg-red-100'
                              }`}>
                                {isProfitable ? (
                                  <TrendingUp className="w-3 h-3 text-green-600" />
                                ) : (
                                  <TrendingDown className="w-3 h-3 text-red-600" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm font-medium truncate ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                                  {project.project_name}
                                </p>
                                <p className={`text-xs truncate ${darkMode ? 'text-[#6A6A6A]' : 'text-gray-400'}`}>
                                  {project.client_name || 'No client'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="text-right ml-2">
                              <p className={`text-sm font-semibold ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(project.profit)}
                              </p>
                              <p className={`text-xs ${marginColor}`}>
                                {project.margin_percent}%
                              </p>
                            </div>
                          </div>
                          
                          {/* Mini progress bar */}
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${isProfitable ? 'bg-green-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, Math.abs(project.margin_percent))}%` }}
                              />
                            </div>
                            <span className={`text-[10px] ${darkMode ? 'text-[#6A6A6A]' : 'text-gray-400'}`}>
                              Rev: {formatCurrency(project.revenue.invoiced)}
                            </span>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div className="space-y-1">
                          <p className="font-medium">{project.project_name}</p>
                          <p>Revenue: {formatFullCurrency(project.revenue.invoiced)}</p>
                          <p>Costs: {formatFullCurrency(project.costs.total)}</p>
                          <p className={isProfitable ? 'text-green-600' : 'text-red-600'}>
                            Profit: {formatFullCurrency(project.profit)} ({project.margin_percent}%)
                          </p>
                          {project.budget > 0 && (
                            <p className="text-xs text-gray-500">
                              Budget: {formatFullCurrency(project.budget)} | Variance: {formatFullCurrency(project.budget_variance)}
                            </p>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
